import { z } from 'zod';

// Validação customizada de URL que aceita URLs de imagens (com ou sem extensão)
const imageUrlSchema = z.string().refine(
  (url) => {
    // Permite string vazia (será filtrada depois)
    if (!url || url.trim() === '') return true;
    try {
      const parsed = new URL(url);
      // Aceita http e https
      return parsed.protocol === 'http:' || parsed.protocol === 'https:';
    } catch {
      return false;
    }
  },
  { message: 'URL inválida. Use uma URL completa começando com http:// ou https://' }
);

// Schema para array de imagens - filtra URLs vazias e valida que tem pelo menos uma
const imageArraySchema = z
  .array(imageUrlSchema)
  .transform((urls) => urls.filter((url) => url && url.trim() !== ''))
  .pipe(z.array(z.url()).min(1, 'Adicione pelo menos uma imagem válida'));

// Apenas pt-BR é obrigatório, outros idiomas são opcionais
const localizedStringSchema = z.object({
  'pt-BR': z.string().min(1, 'Campo obrigatório'),
  'en-US': z.string().optional().default(''),
  'es-ES': z.string().optional().default(''),
});

// Schema para campos completamente opcionais (todos os idiomas podem estar vazios)
const localizedStringOptionalSchema = z.object({
  'pt-BR': z.string().default(''),
  'en-US': z.string().optional().default(''),
  'es-ES': z.string().optional().default(''),
});

const localizedStringArraySchema = z.object({
  'pt-BR': z.array(z.string()),
  'en-US': z.array(z.string()).optional().default([]),
  'es-ES': z.array(z.string()).optional().default([]),
});

const productSpecificationSchema = z.object({
  id: z.string().min(1, 'ID obrigatório'),
  label: localizedStringSchema,
  value: localizedStringSchema,
  order: z.number().int().optional(),
});

const productSpecificationsSchema = z.array(productSpecificationSchema);

export const productCategorySchema = z.enum(['new-drones', 'used-drones', 'buffalo', 'power-citros', 'parts']);

export const productStatusSchema = z.enum(['active', 'inactive', 'draft']);

export const productSchema = z.object({
  id: z.string(),
  category: productCategorySchema,
  name: localizedStringSchema,
  subtitle: localizedStringSchema.optional(),
  slug: z.string().min(3).max(100),
  price: z.number().positive('Preço deve ser maior que zero').optional(),
  image: z.string().url('URL inválida'),
  images: z.array(z.string().url('URL inválida')).min(1, 'Adicione pelo menos uma imagem'),
  description: localizedStringSchema,
  rating: z.number().min(0).max(5).optional(),
  inStock: z.boolean(),
  features: localizedStringArraySchema.optional(),
  condition: localizedStringSchema.optional(),
  specifications: productSpecificationsSchema.optional(),
  warranty: localizedStringSchema.optional(),
  shipping: localizedStringSchema.optional(),
  video: z.url('URL inválida').optional().or(z.literal('')),
  featured: z.boolean(),
  status: productStatusSchema,
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
});

export const productFormSchema = z.object({
  category: productCategorySchema,
  name: localizedStringSchema,
  subtitle: localizedStringOptionalSchema.optional(),
  price: z
    .number()
    .positive('Preço deve ser maior que zero')
    .optional()
    .or(z.literal(0).transform(() => undefined)),
  image: imageUrlSchema,
  images: imageArraySchema,
  description: localizedStringSchema,
  rating: z.number().min(0).max(5).optional(),
  inStock: z.boolean().default(true),
  features: localizedStringArraySchema.optional(),
  condition: localizedStringOptionalSchema.optional(),
  specifications: productSpecificationsSchema.optional(),
  warranty: localizedStringOptionalSchema.optional(),
  shipping: localizedStringOptionalSchema.optional(),
  video: z.url('URL inválida').optional().or(z.literal('')),
  featured: z.boolean().default(false),
  status: productStatusSchema.default('active'),
});

export type ProductFormInput = z.infer<typeof productFormSchema>;
